import { sideText } from '../../lib/recalc';
import { T, say } from './texts';

/*
 * Результат проверочного примера словами (004, FR-036, FR-050, FR-052): ожидаемое примера (`golden_days.expected`) и
 * результат по черновику или возврату. Форма — как у эталона (002 contracts/golden-day-format.md): `{ days: [{ date,
 * state, lines: { Я: "8:00" } }] }`, у эталона demo-0 — `{ state, total: "8,4" }`; длительности — «ч:мм». Интерфейс
 * только переписывает «ч:мм» словами («8 ч», «9 ч 15 мин»): часы считает движок.
 */

const DURATION = /^(\d+):([0-5]\d)$/u;

type Rec = Readonly<Record<string, unknown>>;
const rec = (v: unknown): Rec => (typeof v === 'object' && v !== null ? (v as Rec) : {});

/** «8:00» → «8 ч», «9:15» → «9 ч 15 мин», «0:30» → «30 мин»; число — часы; другое — как есть. */
function durationText(v: unknown): string {
  if (typeof v === 'number') return sideText(v);
  const s = String(v);
  const m = DURATION.exec(s);
  if (!m) return s;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (min === 0) return `${h} ч`;
  return h === 0 ? `${min} мин` : `${h} ч ${min} мин`;
}

/** Строки вне табеля (`offTabel`, движок `OFF_TABEL_ROWS`) словами, как в тексте правила «В какие строки табеля…». */
const OFF_ROW: Readonly<Record<string, string>> = {
  не_засчитано: 'не засчитано',
  вне_графика: 'вне графика',
  кандидат_РВ: 'РВ без распоряжения',
  кандидат_С: 'С без распоряжения',
  явка_без_часов: 'явка без часов',
};

/** Часы вне табеля дня: «не засчитано 25 ч»; нет — пусто. */
function offText(d: Rec): string {
  return Object.entries(rec(d.offTabel))
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .map(([row, v]) => `${OFF_ROW[row] ?? row.replaceAll('_', ' ')} ${durationText(v)}`)
    .join(', ');
}

/** Один день: строки табеля и часы вне табеля, итог дня эталона demo-0 или состояние словами. */
function dayText(day: unknown): string {
  const main = mainText(day);
  const off = offText(rec(day));
  if (off === '') return main;
  return main === '' || main === '—' ? `вне табеля: ${off}` : `${main}, вне табеля: ${off}`;
}

function mainText(day: unknown): string {
  const d = rec(day);
  const lines = Object.entries(rec(d.lines)).filter(
    ([, v]) => typeof v === 'string' || typeof v === 'number',
  );
  if (lines.length === 1) return durationText(lines[0]?.[1]);
  if (lines.length > 1) return lines.map(([row, v]) => `${row} ${durationText(v)}`).join(', ');
  if (typeof d.total === 'string' || typeof d.total === 'number') {
    const total = String(d.total).replace('.', ',');
    return d.state === undefined || d.state === 'ok' ? `${total} ч` : sideText(d);
  }
  return sideText(d);
}

/** `2026-10-05` → `05.10`. */
const shortDate = (iso: unknown) =>
  typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/u.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '';

/** Подпись дня примера: псевдоним человека и дата («В-58, 13.10»); чего нет — того нет. */
const dayLabel = (d: Rec) =>
  [typeof d.person === 'string' ? d.person : '', shortDate(d.date)].filter(Boolean).join(', ');

/** Результат примера словами: один день — «8 ч»; несколько — «В-1, 05.10: 8 ч; В-1, 06.10: нет выхода». */
export function goldenText(v: unknown): string {
  const days = rec(v).days;
  if (!Array.isArray(days)) return dayText(v);
  if (days.length === 1) return dayText(days[0]);
  return days
    .map((d) => {
      const label = dayLabel(rec(d));
      return label ? `${label}: ${dayText(d)}` : dayText(d);
    })
    .join('; ');
}

/** День примера, у которого результат разный: подпись (псевдоним человека, дата), было, станет. */
export interface GoldenDayChange {
  readonly label: string;
  readonly before: string;
  readonly after: string;
}

const daysOf = (v: unknown): readonly unknown[] | null => {
  const days = rec(v).days;
  return Array.isArray(days) ? days : null;
};

/**
 * Дни примера из нескольких дней, у которых результат разный: пары «было — станет» по человеку и дате (у примера
 * объекта несколько людей за одну дату, T106). Пример из одного дня или без дней — `null`: показывается весь результат.
 */
export function goldenDayChanges(before: unknown, after: unknown): GoldenDayChange[] | null {
  const b = daysOf(before);
  const a = daysOf(after);
  if (b === null || a === null || (b.length <= 1 && a.length <= 1)) return null;
  const key = (d: unknown) => `${String(rec(d).person ?? '')}|${String(rec(d).date ?? '')}`;
  const was = new Map(b.map((d) => [key(d), d] as const));
  const now = new Map(a.map((d) => [key(d), d] as const));
  const out: GoldenDayChange[] = [];
  for (const k of new Set([...was.keys(), ...now.keys()])) {
    const x = was.get(k);
    const y = now.get(k);
    const bt = x === undefined ? '—' : dayText(x);
    const at = y === undefined ? '—' : dayText(y);
    if (bt === at) continue;
    out.push({ label: dayLabel(rec(x ?? y)), before: bt, after: at });
  }
  return out;
}

/** «Название: было 10 ч, станет 9 ч»; у примера из нескольких дней — только дни, которые меняются. */
export function goldenChangeText(title: string, before: unknown, after: unknown): string {
  const days = goldenDayChanges(before, after);
  if (days === null) return say.goldenChange(title, goldenText(before), goldenText(after));
  // Часы дней те же, а пример не проходит (пометки, вопросы).
  return days.length === 0 ? say.goldenDetails(title) : say.goldenChangeDays(title, days);
}

/** «изменится: было 10 ч → станет 9 ч»; у примера из нескольких дней — только дни, которые меняются. */
export function goldenWillChangeText(before: unknown, after: unknown): string {
  const days = goldenDayChanges(before, after);
  if (days === null) return say.goldenWillChange(goldenText(before), goldenText(after));
  return days.length === 0 ? T.goldenWillChangeDetails : say.goldenWillChangeDays(days);
}
